import useAssignTaskDrawer from './useAssignTaskDrawer.hook';
import ActionsFooter from '@/shared/StepActionsFooter/StepActionsFooter';
import InputWrapper from '@/shared/InputWrapper/InputWrapper';
import { Alert, Checkbox, InputNumber, Select, Space, Switch } from 'antd';
import UniversalDatePicker from '@/shared/UniversalDatePicker/UniversalDatePicker';
import Dates from '@/constants/Dates';
import dayjs from 'dayjs';
import ExploreAndPlay from '@/shared/ExploreAndPlay/ExploreAndPlay';
import TaskStudentsEditor from '@/shared/TaskStudentsEditor/TaskStudentsEditor';
import TaskActivitiesEditor from '@/shared/TaskActivitiesEditor/TaskActivitiesEditor';
import { Paragraph, Text } from '@/shared/Typography';
import { TasksDrawer } from '@/shared/Drawers';
import Input from '@/shared/Input/Input';
import MediaQueryBreakpoints from '@/constants/MediaQueryBreakpoints';
import { useMediaQuery } from 'react-responsive';
import { StyledAssignTaskDrawer } from './AssignTaskDrawer.styled';

export default function AssignTaskDrawer({ taskTemplate, onClose, onFinish }) {
  const { state, actions } = useAssignTaskDrawer({
    taskTemplate,
    onFinish,
  });

  const dueDateError = state.dueDate?.getTime
    ? state.dueDate.getTime() < state.activationDate.getTime()
    : false;

  const isPhone = useMediaQuery({
    maxWidth: MediaQueryBreakpoints.sm,
  });
  return (
    <TasksDrawer
      title={
        <Text>
          Assign <strong>{taskTemplate.name}</strong>
        </Text>
      }
      width={isPhone ? '100%' : ''}
      onClose={onClose}
      state={state}
      footer={
        <>
          <div className="options">
            {!isPhone && (
              <>
                {state.selectedClasses.length == 1 ? (
                  <Space className="mb pointer">
                    <Switch
                      checked={state.changeStudents}
                      onChange={actions.setChangeStudents}
                    />
                    <Text
                      onClick={() =>
                        actions.setChangeStudents(!state.changeStudents)
                      }
                    >
                      Assign it only to the students I choose
                    </Text>
                  </Space>
                ) : null}
                <Space className="mb pointer">
                  <Switch
                    checked={state.changeActivities}
                    onChange={actions.setChangeActivities}
                  />
                  <Text
                    onClick={() =>
                      actions.setChangeActivities(!state.changeActivities)
                    }
                  >
                    Change the activities of this task
                  </Text>
                </Space>
              </>
            )}
          </div>
          <ActionsFooter
            cancel="Cancel"
            cancelAction={onClose}
            finish="Assign"
            disabled={!state.selectedClasses.length || dueDateError}
            finishAction={actions.assign}
            processing={state.processing}
          />
        </>
      }
    >
      <StyledAssignTaskDrawer>
        <div className="main-panel">
          <Paragraph>
            This will also unlock all theme activities for the students in their{' '}
            <ExploreAndPlay />
          </Paragraph>
          <InputWrapper
            label="Task name:"
            required
            className="no-input-explain"
          >
            <Input
              value={state.name}
              onChange={(e) => actions.setName(e.target.value)}
            />
          </InputWrapper>
          <InputWrapper
            required={true}
            label={`Select the class/es this task will be assigned to:`}
            className="no-input-explain"
          >
            <>
              <Select
                mode="multiple"
                options={state.classes.map((clazz) => ({
                  id: clazz.id,
                  name: clazz.name,
                  provider: clazz.provider,
                }))}
                fieldNames={{
                  label: 'name',
                  value: 'id',
                }}
                value={state.selectedClasses.map(
                  (selectedClass) => selectedClass.id
                )}
                maxTagCount="responsive"
                showArrow={true}
                filterOption={(input, option) =>
                  option.name.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                placeholder={`Select one at least to continue`}
                onSelect={actions.onClassAdded}
                onDeselect={actions.onClassRemoved}
              ></Select>
              {state.selectedClassesAlreadyAssigned.length ? (
                <Alert
                  description={`This task is already assigned to ${
                    state.selectedClasses.length == 1
                      ? 'this class'
                      : `some of the selected classes (${state.selectedClassesAlreadyAssigned
                          .map(
                            (selectedClassAlreadyAssigned) =>
                              selectedClassAlreadyAssigned.name
                          )
                          .join(', ')})`
                  }`}
                />
              ) : null}
            </>
          </InputWrapper>
          <InputWrapper
            label="Starts at:"
            required
            className="no-input-explain"
          >
            <UniversalDatePicker
              value={state.activationDate}
              onChange={actions.setActivationDate}
              format={Dates.defaultFormat}
              disabledDate={(current) =>
                current && current < dayjs().subtract(1, 'day').endOf('day')
              }
              allowClear={false}
            />
          </InputWrapper>
          <InputWrapper
            label="Due date:"
            hasError={dueDateError}
            className="no-input-explain"
            /* help={'Due date has to be greater than starts at date'} */
          >
            <UniversalDatePicker
              value={state.dueDate}
              onChange={actions.setDueDate}
              format={Dates.defaultFormat}
              disabledDate={(current) =>
                (current && current < state.activationDate?.getTime()) ||
                (current && current < dayjs().subtract(1, 'day').endOf('day'))
              }
            />
          </InputWrapper>
          <div className="checkbox-container">
            <Checkbox
              checked={state.allowsOverdue}
              onChange={(e) => actions.setAllowsOverdue(e.target.checked)}
              disabled={!state.dueDate}
            >
              Let the students finish or repeat activities after due date
            </Checkbox>
          </div>
          {state.isSomeGoogleClass ? (
            <div className="highlighted-container">
              <Checkbox
                checked={state.sendToGoogle}
                onChange={(e) => actions.setSendToGoogle(e.target.checked)}
              >
                Post to Google Classroom
              </Checkbox>
              <InputWrapper label="Max. points in Google Classroom:">
                <div>
                  <InputNumber
                    value={state.googleMaxPoints}
                    onChange={actions.setGoogleMaxPoints}
                    disabled={!state.sendToGoogle}
                    width={10}
                  />
                </div>
              </InputWrapper>
            </div>
          ) : null}
        </div>
      </StyledAssignTaskDrawer>
      {state.changeStudents && state.selectedClasses.length == 1 && (
        <TaskStudentsEditor
          classId={state.selectedClasses[0].id}
          selectedStudents={state.selectedStudents}
          setSelectedStudents={actions.setSelectedStudents}
        />
      )}
      {state.changeActivities && (
        <TaskActivitiesEditor
          themeId={taskTemplate.themeId}
          taskTemplateId={taskTemplate.id}
          taskActivities={state.taskActivities}
          setTaskActivities={actions.setTaskActivities}
        />
      )}
    </TasksDrawer>
  );
}
